import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { MdAccessibilityNew } from "react-icons/md";
import AuthContext from "../../store/auth-context";
import PageHeading from "../Util/PageHeading";
import { showsInvestorShell } from "../Util/PublicPageLayout";
import styles from "./AccessibilityHelp.module.css";

/**
 * Accessibility help (v1.3 ticket 12, RCMC-15): who to contact for help using the portal, how fast RCMC responds, how
 * to escalate, and SEBI SCORES. A public page (routes.js "/accessibility-help"): a sign-in barrier must never stop an
 * Investor asking for help, so it opens signed out too. Util/PublicPageLayout puts it in the portal shell, with the
 * investor navigation for a signed-in Investor and without it for anyone else.
 *
 * The contact details repeat the "Digital accessibility" box on the website's contact page
 * (Website QW/…/rcmcdelhi.com/contact.php). A change of officer, number, email or hours must be made in BOTH places.
 * The response target is the owner's ruling Q9 (12 Oct 2026): 1 business day to acknowledge, 7 to resolve.
 *
 * Links stay in this tab (no target="_blank"): no unannounced new window.
 */
/** The way on from the page when it shows without navigation, or null: signed out, to sign in; signed in as someone
 *  without the investor navigation (Operations, an Investor awaiting approval), back to the portal (sign-in sends each
 *  to their own screen). Nothing while a stored session's user details are on their way: the navigation may follow. */
function wayOn(authTokens, user) {
  if (!authTokens) return "Sign in to the investor portal";
  if (user && !showsInvestorShell(user)) return "Return to the portal";
  return null;
}

const AccessibilityHelp = () => {
  const { authTokens, user } = useContext(AuthContext);
  const wayOnLabel = wayOn(authTokens, user);

  return (
    <div className={styles["page"]}>
      <PageHeading icon={<MdAccessibilityNew style={{ marginRight: "5px" }} />}>Accessibility help</PageHeading>
      <div className={styles["content"]}>
        <p>
          If you have difficulty using this portal or our website, or you need help completing a request another
          way, contact our Nodal Officer for digital accessibility directly. You do not need to sign in to ask.
        </p>

        <section>
          <h2>Nodal Officer — Digital Accessibility</h2>
          <p className={styles["name"]}>Abhinav Sharma</p>
          <ul className={styles["contacts"]}>
            <li>Phone: <a href="tel:+918800807292">+91 88008 07292</a></li>
            <li>Email: <a href="mailto:abhinav.sharma@rcmcdelhi.com">abhinav.sharma@rcmcdelhi.com</a></li>
          </ul>
        </section>

        <section>
          <h2>When we respond</h2>
          <p>Your request is acknowledged within 1 business day and resolved within 7 business days.</p>
          {/* The website's business-hours line, word for word */}
          <p>Business hours are Monday–Friday, 10am–6pm IST, excluding public holidays in India.</p>
        </section>

        <section>
          <h2>If it is not resolved</h2>
          <p>
            Escalate to Alok Sharma, Compliance Officer —{" "}
            <a href="mailto:alok.sharma@rcmcdelhi.com">alok.sharma@rcmcdelhi.com</a>.
          </p>
          <p>
            You can also raise the matter on <a href="https://scores.sebi.gov.in/">SEBI SCORES</a>, under the
            “Accessibility” category. We close an accessibility matter by fixing the barrier, not by replying to it.
          </p>
        </section>

        <section>
          <h2>Write to us</h2>
          <p>
            Prefer a form? Use the <a href="https://rcmcdelhi.com/contact#contact-form">contact form on rcmcdelhi.com</a>{" "}
            and choose “Digital accessibility assistance”.
          </p>
        </section>

        {wayOnLabel && (
          <p className={styles["way-on"]}>
            <Link to="/login">{wayOnLabel}</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default AccessibilityHelp;
